import {Injectable} from '@angular/core';
import {ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable} from "rxjs";
import {UserRoles} from "../user/user-roles";
import {RouteData} from "../shared/services/route-data/route-data";
import * as fromStore from '../store';
import {DbStoreState} from '../store';
import {first, switchMap} from "rxjs/operators";
import {User} from '../user/user';
import {Store} from '@ngrx/store';
import {UserService} from '../user/user.service';
import {combineLatest} from "rxjs";
import {HostingService} from "../websites/website/hosting/hosting.service";
import {DbModalService} from "../modules/modal/modal";
import {UserSubscription} from "../user/user-subscription";
import {DialogService} from "../../ng2-bootstrap-modal/dialog.service";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";

@Injectable()
export class SignUpGuard implements CanActivate {

  private user: User;
  private userSubscription: UserSubscription;
  private subscriptionId: number;
  private templateId: number;
  private pluginSlug: string;
  private signUpFromFreePlugin: string;
  private currentWorkspaceId: number;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<DbStoreState>,
    private _userService: UserService,
    private _hostingService: HostingService,
    private _dbModalService: DbModalService,
    private _dialogService: DialogService,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return new Promise<boolean>((resolve) => {
      this._store.select(fromStore.getUserLoaded)
        .pipe(
          first(loaded => loaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getWorkspaceIdEntities),
          )),
          first()
        )
        .subscribe(([user, userSubscription, workspaceIdEntities]) => {
          this.user = user;
          this.userSubscription = userSubscription;
          this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || workspaceIdEntities.ownerWorkspaceId;
          const role: string = user ? UserRoles.user : UserRoles.guest;
          const routeData: RouteData = new RouteData(next.data);
          const isAuthorized: boolean = routeData.roles.indexOf(role) !== -1 || routeData.roles.indexOf(UserRoles.all) !== -1;

          const queryParams: any = next.queryParams;
          if (queryParams.send_event) {
            this._sendPGEvent(queryParams.tenweb_action, queryParams.has_account, queryParams.return_url, workspaceIdEntities && workspaceIdEntities.currentWorkspaceId);
          }
          if (queryParams.two_comes_from) {
            this._sendComesFromEvent(next.queryParams['two_comes_from'], workspaceIdEntities && workspaceIdEntities.currentWorkspaceId);
          }
          if (queryParams) {
            this.subscriptionId = parseInt(queryParams.subscr_id, 10);
            const referralHashFromURL: any = queryParams.referral_hash ? queryParams.referral_hash : null;
            this.pluginSlug = queryParams.plugin_slug;
            this.templateId = queryParams.template_id;
            this.signUpFromFreePlugin = queryParams.sign_up_from_free_plugin;
          }

          if ((queryParams.magic_data && queryParams.navigate_to)) {
            if (role === UserRoles.user) {
              return this._router.navigate([`/${queryParams.navigate_to}`]);
            }
            return this._router.navigate(['/login'], {queryParams: {
                ...queryParams,
                returnUrl: `/${queryParams.navigate_to}`
              }
            });
          }
          if (role === UserRoles.guest && (queryParams.has_account)) {
            this._router.navigate(['/login'], {queryParams});
          }
          if (isAuthorized) {
            resolve(true);
            return;
          }
          if (role === UserRoles.guest && !this.templateId) {
            this._router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
          }
          else {
            if ( !this.templateId ) {
              if (queryParams.navigate_to) {
                return this._router.navigate([`/${queryParams.navigate_to}`]);
              }
              if (this.subscriptionId && !queryParams.return_url) {
                this._router.navigate(['/websites'],
                  {
                    queryParams: {
                      subscr_id: this.subscriptionId,
                      plugin_slug: this.pluginSlug,
                      utm_source: queryParams.utm_source,
                      site_url: queryParams.site_url,
                    }
                  });
              }
              else if (queryParams.plugin_id === '200') {
                this._router.navigateByUrl('/websites?plugin_id=200');
              }
              else {
                this._router.navigate(['/websites'], {queryParams: next.queryParams});
                return;
              }
            }
          }

          resolve(false);
        });
    });
  }

  private _sendComesFromEvent(label: string, workspaceId): void {
    const analyticsData = {
      eventCategory: 'Dashboard Pageview',
      eventAction: 'From Booster Plugin',
      eventLabel: label,
    };
    if (workspaceId) {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }
  private _sendPGEvent(action: string, hasAccount, returnUrl, workspaceId): void {
    let label = 'No Account';
    if (hasAccount) {
      label = returnUrl ? 'Account without Connect' : 'Connected';
    }
    const analyticsData = {
      eventCategory: 'Dashboard Pageview',
      eventAction: action,
      eventLabel: label,
    };
    if (workspaceId) {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }
}
